// src/pages/OfferCodesPage.js (admin)
// Create, manage, and deactivate offer codes.
// Codes are stored in the offerCodes Firestore collection.
import React, { useState, useEffect } from 'react';
import {
  collection, doc, addDoc, updateDoc,
  serverTimestamp, orderBy, query, onSnapshot,
} from 'firebase/firestore';
import { db } from '../firebase';
import { OFFER_CODE_TYPES } from '../plans';

const TYPE_LABELS = {
  free_period: 'Free Pro Period',
  percent_off: '% Discount',
  fixed_off:   '$ Discount',
};

const fmtDate = (ts) => {
  if (!ts?.seconds) return '—';
  return new Date(ts.seconds * 1000).toLocaleDateString('en-CA', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
};

const generateCode = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  return Array.from({ length: 8 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
};

export default function OfferCodesPage() {
  const [codes,     setCodes]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [showForm,  setShowForm]  = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [toggling,  setToggling]  = useState(null);

  const [form, setForm] = useState({
    code:              generateCode(),
    type:              OFFER_CODE_TYPES.free_period,
    value:             3,          // months (free_period) | percent | dollars
    durationMonths:    null,       // how long percent/fixed discount lasts
    maxUses:           1,          // null = unlimited
    perHirerLimit:     1,
    eligibleHirerIds:  '',         // comma-separated UIDs, empty = anyone
    expiresAt:         '',         // date string, empty = no expiry
    notes:             '',
  });

  const updateForm = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  // Real-time listener — wait for auth before subscribing
  useEffect(() => {
    const { auth } = require('../firebase');
    if (!auth.currentUser) { setLoading(false); return; }
    setLoading(true);
    const unsub = onSnapshot(
      query(collection(db, 'offerCodes'), orderBy('createdAt', 'desc')),
      (snap) => {
        setCodes(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => { console.warn('offerCodes snapshot:', err.message); setLoading(false); }
    );
    return unsub;
  }, []);

  // load() still used after create/toggle actions
  const load = () => {}; // no-op — onSnapshot handles updates

  // ── Create code ───────────────────────────────────────────────────────────
  const handleCreate = async () => {
    if (!form.code.trim()) { alert('Enter a code.'); return; }
    if (!form.value || Number(form.value) <= 0) { alert('Enter a valid value.'); return; }
    setSaving(true);
    try {
      const eligibleIds = form.eligibleHirerIds
        .split(',').map(s => s.trim()).filter(Boolean);
      const expiresAt = form.expiresAt
        ? new Date(form.expiresAt + 'T23:59:59')
        : null;

      await addDoc(collection(db, 'offerCodes'), {
        code:             form.code.trim().toUpperCase(),
        type:             form.type,
        value:            Number(form.value),
        durationMonths:   form.durationMonths ? Number(form.durationMonths) : null,
        maxUses:          form.maxUses === '' || form.maxUses === null ? null : Number(form.maxUses),
        perHirerLimit:    Number(form.perHirerLimit) || 1,
        eligibleHirerIds: eligibleIds,
        expiresAt:        expiresAt ? expiresAt : null,
        notes:            form.notes.trim(),
        active:           true,
        redemptionCount:  0,
        createdAt:        serverTimestamp(),
        createdBy:        'admin',
      });
      setForm(prev => ({ ...prev, code: generateCode(), notes: '', eligibleHirerIds: '' }));
      setShowForm(false);
      load();
    } catch (e) {
      alert('Error creating code. Please try again.');
      console.error(e);
    } finally { setSaving(false); }
  };

  // ── Toggle active ─────────────────────────────────────────────────────────
  const toggleActive = async (code) => {
    if (!window.confirm(
      `${code.active ? 'Deactivate' : 'Reactivate'} code "${code.code}"?\n` +
      (code.active ? 'No new redemptions will be accepted.' : 'Code will accept redemptions again.')
    )) return;
    setToggling(code.id);
    try {
      await updateDoc(doc(db, 'offerCodes', code.id), { active: !code.active });
      load();
    } catch (e) { alert('Error updating code.'); }
    finally { setToggling(null); }
  };

  // ── Benefit summary string ────────────────────────────────────────────────
  const benefitSummary = (code) => {
    if (code.type === OFFER_CODE_TYPES.free_period)
      return `${code.value} month${code.value > 1 ? 's' : ''} Pro free`;
    if (code.type === OFFER_CODE_TYPES.percent_off)
      return `${code.value}% off${code.durationMonths ? ` × ${code.durationMonths}mo` : ''}`;
    if (code.type === OFFER_CODE_TYPES.fixed_off)
      return `$${code.value} off${code.durationMonths ? ` × ${code.durationMonths}mo` : ''}`;
    return '—';
  };

  return (
    <div className="animate-fade">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28, flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h1 style={{ fontFamily: 'Syne', fontSize: 28, fontWeight: 800, color: 'var(--text)', margin: 0 }}>Offer Codes</h1>
          <p style={{ color: 'var(--text-muted)', marginTop: 4, fontSize: 14 }}>
            Create promo codes to give hirers free Pro access or discounts
          </p>
        </div>
        <button
          onClick={() => setShowForm(v => !v)}
          style={{ background: '#F97316', border: 'none', borderRadius: 10, padding: '10px 20px', color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}
        >
          {showForm ? '✕ Cancel' : '+ New Code'}
        </button>
      </div>

      {/* Create form */}
      {showForm && (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16, padding: 28, marginBottom: 28 }}>
          <h3 style={{ fontFamily: 'Syne', color: 'var(--text)', margin: '0 0 20px' }}>Create Offer Code</h3>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            {/* Code */}
            <Field label="Code *">
              <div style={{ display: 'flex', gap: 8 }}>
                <input value={form.code} onChange={e => updateForm('code', e.target.value.toUpperCase())}
                  style={inputStyle} placeholder="e.g. WELCOME2026" />
                <button onClick={() => updateForm('code', generateCode())}
                  style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8, padding: '0 12px', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 12 }}>
                  🔀
                </button>
              </div>
            </Field>

            {/* Type */}
            <Field label="Benefit Type *">
              <select value={form.type} onChange={e => updateForm('type', e.target.value)} style={inputStyle}>
                {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>

            {/* Value */}
            <Field label={
              form.type === OFFER_CODE_TYPES.free_period ? 'Number of Free Months *' :
              form.type === OFFER_CODE_TYPES.percent_off ? 'Discount % *' : 'Discount Amount ($) *'
            }>
              <input type="number" min="1" value={form.value}
                onChange={e => updateForm('value', e.target.value)} style={inputStyle} />
            </Field>

            {/* Duration (not for free_period) */}
            {form.type !== OFFER_CODE_TYPES.free_period && (
              <Field label="Discount Lasts (months, blank = first payment only)">
                <input type="number" min="1" value={form.durationMonths || ''}
                  onChange={e => updateForm('durationMonths', e.target.value || null)}
                  style={inputStyle} placeholder="e.g. 3" />
              </Field>
            )}

            {/* Max uses */}
            <Field label="Max Total Uses (blank = unlimited)">
              <input type="number" min="1" value={form.maxUses ?? ''}
                onChange={e => updateForm('maxUses', e.target.value || null)}
                style={inputStyle} placeholder="e.g. 50" />
            </Field>

            {/* Per-hirer limit */}
            <Field label="Uses Per Hirer">
              <input type="number" min="1" value={form.perHirerLimit}
                onChange={e => updateForm('perHirerLimit', e.target.value)} style={inputStyle} />
            </Field>

            {/* Expiry */}
            <Field label="Expiry Date (blank = no expiry)">
              <input type="date" value={form.expiresAt}
                onChange={e => updateForm('expiresAt', e.target.value)} style={inputStyle} />
            </Field>

            {/* Eligible hirers */}
            <Field label="Restrict to Hirer UIDs (comma-separated, blank = anyone)">
              <input value={form.eligibleHirerIds}
                onChange={e => updateForm('eligibleHirerIds', e.target.value)}
                style={inputStyle} placeholder="uid1, uid2, uid3" />
            </Field>
          </div>

          {/* Notes */}
          <Field label="Internal Notes">
            <input value={form.notes} onChange={e => updateForm('notes', e.target.value)}
              style={inputStyle} placeholder="e.g. Conference giveaway May 2026" />
          </Field>

          {/* Preview */}
          <div style={{ background: 'rgba(249,115,22,0.07)', border: '1px solid rgba(249,115,22,0.2)', borderRadius: 10, padding: '12px 16px', margin: '16px 0', fontSize: 13, color: 'var(--text-secondary)' }}>
            <strong style={{ color: 'var(--text)' }}>Preview: </strong>
            Code <strong style={{ color: '#F97316', fontFamily: 'monospace' }}>{form.code}</strong> gives{' '}
            {form.type === OFFER_CODE_TYPES.free_period && `${form.value} month${form.value > 1 ? 's' : ''} of Pro free`}
            {form.type === OFFER_CODE_TYPES.percent_off && `${form.value}% off${form.durationMonths ? ` for ${form.durationMonths} months` : ' first payment'}`}
            {form.type === OFFER_CODE_TYPES.fixed_off   && `$${form.value} off${form.durationMonths ? ` for ${form.durationMonths} months` : ' first payment'}`}
            {form.maxUses ? ` · max ${form.maxUses} uses` : ' · unlimited uses'}
            {form.expiresAt ? ` · expires ${form.expiresAt}` : ' · no expiry'}
            {form.eligibleHirerIds && ' · restricted to specific hirers'}
          </div>

          <button onClick={handleCreate} disabled={saving}
            style={{ background: '#F97316', border: 'none', borderRadius: 10, padding: '12px 28px', color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Creating…' : '✓ Create Code'}
          </button>
        </div>
      )}

      {/* Codes table */}
      {loading ? (
        <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 60 }}>Loading…</div>
      ) : codes.length === 0 ? (
        <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 60, background: 'var(--surface)', borderRadius: 16, border: '1px solid var(--surface2)' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🏷️</div>
          <div>No offer codes yet. Create one above.</div>
        </div>
      ) : (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16, overflow: 'hidden' }}>
          {/* Table header */}
          <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr 100px 80px 100px 90px 100px', gap: 0, padding: '12px 20px', background: 'var(--surface2)', borderBottom: '1px solid var(--border)' }}>
            {['Code', 'Benefit', 'Uses', 'Per Hirer', 'Expiry', 'Status', 'Action'].map(h => (
              <span key={h} style={{ color: 'var(--text-muted)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</span>
            ))}
          </div>

          {codes.map((code, i) => (
            <div key={code.id}
              style={{ display: 'grid', gridTemplateColumns: '160px 1fr 100px 80px 100px 90px 100px', gap: 0, padding: '14px 20px', borderBottom: '1px solid var(--border)', opacity: code.active ? 1 : 0.5, background: i % 2 === 0 ? 'transparent' : 'var(--surface2)' }}>

              {/* Code */}
              <div>
                <div style={{ fontFamily: 'monospace', fontWeight: 800, color: 'var(--text)', fontSize: 14, letterSpacing: 1 }}>{code.code}</div>
                {code.notes && <div style={{ color: 'var(--text-muted)', fontSize: 11, marginTop: 2 }}>{code.notes}</div>}
                <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>Created {fmtDate(code.createdAt)}</div>
              </div>

              {/* Benefit */}
              <div>
                <div style={{ color: 'var(--text)', fontWeight: 600, fontSize: 13 }}>{benefitSummary(code)}</div>
                <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>{TYPE_LABELS[code.type]}</div>
                {code.eligibleHirerIds?.length > 0 && (
                  <div style={{ color: '#3B82F6', fontSize: 11 }}>🔒 Restricted to {code.eligibleHirerIds.length} hirer{code.eligibleHirerIds.length > 1 ? 's' : ''}</div>
                )}
              </div>

              {/* Uses */}
              <div style={{ color: 'var(--text)', fontSize: 13 }}>
                {code.redemptionCount || 0}
                {code.maxUses !== null ? ` / ${code.maxUses}` : ' / ∞'}
              </div>

              {/* Per hirer */}
              <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{code.perHirerLimit || 1}</div>

              {/* Expiry */}
              <div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
                {code.expiresAt?.seconds
                  ? fmtDate(code.expiresAt)
                  : <span style={{ color: 'var(--text-muted)' }}>No expiry</span>}
              </div>

              {/* Status */}
              <div>
                <span style={{
                  background: code.active ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
                  border: `1px solid ${code.active ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`,
                  color: code.active ? '#22C55E' : '#EF4444',
                  borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 700,
                }}>
                  {code.active ? '✓ Active' : '✗ Inactive'}
                </span>
              </div>

              {/* Action */}
              <div>
                <button
                  onClick={() => toggleActive(code)}
                  disabled={toggling === code.id}
                  style={{
                    background: code.active ? 'rgba(239,68,68,0.1)' : 'rgba(34,197,94,0.1)',
                    border: `1px solid ${code.active ? 'rgba(239,68,68,0.3)' : 'rgba(34,197,94,0.3)'}`,
                    color: code.active ? '#EF4444' : '#22C55E',
                    borderRadius: 8, padding: '5px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  {toggling === code.id ? '…' : code.active ? 'Deactivate' : 'Reactivate'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', color: 'var(--text-secondary)', fontSize: 11, fontWeight: 700, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </label>
      {children}
    </div>
  );
}

const inputStyle = {
  width: '100%', background: 'var(--surface2)', border: '1px solid var(--border)',
  borderRadius: 8, padding: '9px 12px', color: 'var(--text)', fontSize: 13,
  outline: 'none', boxSizing: 'border-box',
};